/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import Menu from '@material-ui/core/Menu';
import {makeStyles} from '@material-ui/core/styles';
import {dlsZIndex} from '@ion/dls/src/theme/theme';
import {MenuItems} from '../../types/components';
import navStyles from './Navigation.module.scss';

// defines the props for secondary navigation container
export interface SecondaryNavigationTypes {
  isDesktop: boolean;
  createChildren: (onSetPage: (page: string, index: number) => void,
    currentPage: string, nestedMenuItems: MenuItems) => JSX.Element;
  anchorEl: HTMLElement;
  nestedMenuItems?: MenuItems;
  onSetPage(page: string, index: number): void;
  currentPage: string;
  onMouseMove?: React.MouseEventHandler<HTMLDivElement>;
};

//defines the styles used by secondary navigation container
export const useStyles = makeStyles(theme => ({
  listContainer : {
    position : 'absolute',
    left     : '115px',
    top      : '0px',
    zIndex   : dlsZIndex('secondaryNavigationListContainer')
  },
  secondaryNavRoot : {
    zIndex : `${dlsZIndex('secondaryNavigationPopoverRoot')} !important` as any
  }
}));

/**
 * @SecondaryNavigation
 *
 * Defines the SecondaryNavigation component. This component acts as a wrapper/container
 * for the secondary navigation items and controls the same between desktop and mobile.
 *
 * ```typescript
 *
 *   const nestedMenuItems = [
 *     {label: t('subPage1:title), pages: '/page1/subPage1' },
 *     {label: t('subPage2:title), pages: '/page1/subPage2' },
 *   ];
 *
 *   <SecondaryNavigation onSetPage={onSetPage}
 *     currentPage={currentPage}
 *     isDesktop
 *     desktopHorizontalPosition={114}
 *     desktopVerticalPosition={400}
 *     nestedMenuItems={nestedMenuItems}>
 *      {(onSetPage, currentPage, nestedMenuItems = item?.pages) => {
 *        return (<SecondaryNavigationItems onSetPage={onSetPage}
 *           currentPage={currentPage}
 *           nestedMenuItems={nestedMenuItems} />);
 *         }
 *         }
 *   </SecondaryNavigation>
 *
 * ```
 *
 * @param {boolean} isDesktop: true uses menu component to render menu options
 * for desktop, false uses a list for mobile.
 *
 * @param {HTMLElement} anchorEl: html element that serves as anchor point for the menu position
 * in desktop version.
 *
 * @param {Function} children: a function which returns a component that renders a list of menu items.
 */
export const SecondaryNavigation = ({
  isDesktop,
  createChildren,
  onSetPage,
  currentPage,
  nestedMenuItems,
  anchorEl,
  onMouseMove
}: SecondaryNavigationTypes) => {
  const classes = useStyles();

  return (
    isDesktop ? (
      <Menu open
        className={navStyles.secondaryNavWrapper}
        PopoverClasses={{
          root : classes.secondaryNavRoot
        }}
        onMouseMove={onMouseMove}
        anchorEl={anchorEl}
        anchorOrigin={{vertical: 'top', horizontal: 'right'}}
        transformOrigin={{vertical: 'top', horizontal: 'left'}}>
        {createChildren(onSetPage, currentPage, nestedMenuItems)}
      </Menu>
    )
      : (
        <div className={classes.listContainer}>
          {createChildren(onSetPage, currentPage, nestedMenuItems)}
        </div>
      )
  );
};

export default SecondaryNavigation;
